// Client wrappers for the Phase-3 Platform Admin callables:
// WhatsApp connection health, template queue, broadcast oversight,
// support messaging, and API key oversight. All platform-admin guarded.
import { call } from './callable';


// ── health board ─────────────────────────────────────────────────────────────
export interface HealthRow {
  tenantId: string;
  tenantName: string;
  status: string;
  /** One row per connected number; null = tenant has none yet. */
  integrationId?: string | null;
  label?: string | null;
  coexistence?: boolean;
  connected: boolean;
  isLive: boolean;
  isPaused: boolean;
  hasToken: boolean;
  hasAppSecret: boolean;
  phoneNumberId: string | null;
  verifiedPhoneE164: string | null;
  qualityRating: string | null;
  messagingLimit: string | null;
  nameStatus: string | null;
  metaError: string | null;
  severity: number;
}

/** Per TENANT (a tenant counts once however many numbers it has); `numbers` = connected numbers. */
export interface HealthSummary { total: number; connected: number; live: number; problems: number; numbers?: number }

const padminOpsC = call<any, any>('padminOps');

export const healthBoard = async () => (await padminOpsC({ action: 'healthBoard' })).data;

// ── template queue ───────────────────────────────────────────────────────────
export interface AdminTemplate {
  docId: string;
  tenantId: string;
  tenantName: string;
  name: string;
  language: string;
  category: string;
  status: string;
  metaTemplateId: string | null;
  createdAt: number;
  lastSyncedAt: number | null;
}

export const listTemplates = async (status?: string, limit?: number) =>
  (await padminOpsC({ action: 'listTemplates', status, limit })).data;
export const resyncTemplate = async (tenantId: string, docId: string) =>
  (await padminOpsC({ action: 'resyncTemplate', tenantId, docId })).data;

// ── broadcast oversight ──────────────────────────────────────────────────────
export interface AdminBroadcast {
  id: string;
  tenantId: string;
  tenantName: string;
  name: string;
  status: string;
  kind: string | null;
  templateName: string | null;
  audience: number;
  sent: number;
  failed: number;
  scheduledAt: number | null;
  createdAt: number;
  pausedByAdmin: boolean;
}

export const listBroadcasts = async (active?: boolean, limit?: number) =>
  (await padminOpsC({ action: 'listBroadcasts', active, limit })).data;
export const controlBroadcast = async (p: { tenantId: string; broadcastId: string; action: 'pause' | 'resume' | 'cancel'; reason: string }) =>
  (await padminOpsC({ action: 'controlBroadcast', tenantId: p.tenantId, broadcastId: p.broadcastId, controlAction: p.action, reason: p.reason })).data;

// ── support message ──────────────────────────────────────────────────────────
export const sendSupportMessage = async (p: { tenantId: string; to: string; body: string; reason: string }) =>
  (await padminOpsC({ action: 'sendSupportMessage', ...p })).data;

// ── API keys ─────────────────────────────────────────────────────────────────
export interface AdminApiKey {
  id: string;
  label: string;
  prefix: string;
  last4: string;
  revoked: boolean;
  createdAt: number;
  lastUsedAt: number | null;
}

export const listApiKeys = async (tenantId: string) =>
  (await padminOpsC({ action: 'listApiKeys', tenantId })).data.keys;
export const revokeApiKey = async (p: { tenantId: string; keyId: string; reason: string }) =>
  (await padminOpsC({ action: 'revokeApiKey', ...p })).data;
